import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import cn from '@/utils/cn';

import StrapiMedia from '@/components/atoms/StrapiMedia/StrapiMedia';
import Picto from '@/components/atoms/Picto/Picto';
import Blob from '@/components/atoms/Blob/Blob';

import { BubbleShapeEnum } from '@/enum/BubbleShapeEnum';
import { BubbleModeEnum } from '@/enum/BubbleModeEnum';
import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import styles from './Menu.module.css';
import { HeaderMenuType } from '../HeaderType';

interface MenuEntryProps {
  entry: HeaderMenuType;
  forceDropdownOpen?: boolean;
}

const CLOSE_DELAY_MS = 200;

function MenuEntry({ entry, forceDropdownOpen = false }: MenuEntryProps) {
  const [open, setOpen] = useState(forceDropdownOpen);
  const closeTimerRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const gridCols = useMemo(() => {
    const d = entry.dropdown;
    if (!d) return 0;
    let n = 0;
    if (d.overview) n += 1;
    if (d.linkGroup?.length) n += d.linkGroup.length;
    if (d.picture) n += 1;
    return n;
  }, [entry.dropdown]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openDropdown = () => {
    if (!entry.dropdown) return;
    clearCloseTimer();
    setOpen(true);
  };

  const scheduleClose = () => {
    if (forceDropdownOpen) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!open || forceDropdownOpen) return undefined;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open || !entry.dropdown || forceDropdownOpen) return undefined;

    const el = wrapperRef.current;
    if (!el) return undefined;

    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && el.contains(next)) return;
      setOpen(false);
    };

    el.addEventListener('focusout', onFocusOut);
    return () => el.removeEventListener('focusout', onFocusOut);
  }, [open, entry.dropdown]);

  return (
    <div
      ref={wrapperRef}
      className={cn([styles.entryWrapper, entry.dropdown && open ? styles.entryWrapperOpen : ''])}
      onMouseEnter={() => {
        if (entry.dropdown && !forceDropdownOpen) openDropdown();
      }}
      onMouseLeave={() => {
        if (open && entry.dropdown) scheduleClose();
      }}
    >
      {entry.link ? (
        <Link
          to={entry.link}
          className={cn([
            styles.accessor,
            entry.highlight ? styles.accessorHighlight : '',
            entry.dropdown ? styles.accessorWithDropdown : '',
          ])}
        >
          {entry.label}
          {entry.dropdown && (
            <Picto
              icon="chevronDown"
              width={14}
              className={cn([styles.dropdownChevron, open && styles.dropdownChevronOpen])}
              aria-hidden
            />
          )}
        </Link>
      ) : (
        <button
          type="button"
          className={cn([
            styles.accessor,
            open && styles.active,
            entry.highlight ? styles.accessorHighlight : '',
            entry.dropdown ? styles.accessorWithDropdown : '',
          ])}
          tabIndex={0}
          aria-expanded={Boolean(entry.dropdown && open)}
          aria-haspopup={entry.dropdown ? 'menu' : undefined}
          onFocus={() => {
            if (entry.dropdown && !open && !forceDropdownOpen) openDropdown();
          }}
        >
          {entry.label}
          {entry.dropdown && (
            <Picto
              icon="chevronDown"
              width={14}
              className={cn([styles.dropdownChevron, open && styles.dropdownChevronOpen])}
              aria-hidden
            />
          )}
        </button>
      )}

      {entry.dropdown && (
        <div className={cn([styles.dropdown, open && styles.dropdownOpen])} aria-hidden={!open}>
          <div className={styles.content} style={{ gridTemplateColumns: `repeat(${Math.max(gridCols, 1)}, 1fr)` }}>
            {entry.dropdown.overview && (
              <div className={styles.contentColumn}>
                <span className={cn([styles.title, styles.titleOverview])}>{entry.dropdown.overview.title}</span>
                <p>{entry.dropdown.overview.description}</p>
              </div>
            )}

            {entry.dropdown.linkGroup &&
              entry.dropdown.linkGroup.map((linkColumn) => (
                <div key={linkColumn.label} className={styles.contentColumn}>
                  <h5 className={styles.title}>{linkColumn.label}</h5>
                  <ul className={styles.listLinks}>
                    {linkColumn.links?.map((link) => (
                      <li key={link.label} className={styles.link}>
                        <Picto className={styles.icon} icon="arrowRight" width={14} />
                        <Link to={link.link}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

            {entry.dropdown.picture && (
              <div className={styles.contentColumn}>
                <Blob
                  className={cn([styles.blob])}
                  color={ColorThemeEnum.WHITE}
                  theme={null}
                  shape={BubbleShapeEnum.SHAPE_7}
                  mode={BubbleModeEnum.CLIPPED}
                >
                  <StrapiMedia image={entry.dropdown.picture} format="medium" className={styles.image} fill />
                </Blob>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuEntry;
